import React from 'react';
import { observer } from 'mobx-react';
import { inject } from '../../../utils';
import { computed, observable } from 'mobx';
import { ThemeableComponent } from '../../../utils/theme/ThemeableComponent';
import { DropdownItem } from '../../../models/components/Dropdown/DropdownItem';
import DropdownListItem from './DropdownListItem';
import { camelCase } from 'change-case';
import { withTheme } from 'styled-components';
import DropdownList from '../StyledComponents/DropdownList';
import { DropdownMenuThemeKeys } from '../Models';
import DropdownMenuWrapper from '../StyledComponents/DropdownMenuWrapper';
import { KeyboardEvents } from '../../../utils/accessibility/KeyboardEvents';
import { ChildReferences } from '../../../utils/accessibility/ChildReferences';
import { SuperscriptScope } from '../../../stores';

interface DropdownMenuProps {
    items: Array<DropdownItem>;
    itemLabelFn: (item: DropdownItem) => string;
    handleOnClick: (
        event: React.SyntheticEvent<HTMLElement>,
        item: DropdownItem,
        index: number
    ) => void;
    item: DropdownItem;
    index: number;
    selectedItemIndex: number;
    pageKeyTraverseSize: number;
    dropdownMenuRef: (element: HTMLElement) => void;
    isOpen: boolean;
    keyboardEvents: KeyboardEvents<HTMLButtonElement>;
    showDropdown: boolean;
    childReferences: ChildReferences<HTMLLIElement>;
    superscriptScope: SuperscriptScope;
}

@inject('dataStore')
@observer
class DropdownMenu extends ThemeableComponent<
    DropdownMenuProps,
    {},
    DropdownMenuThemeKeys
> {
    @observable.ref rawSelectedItemIndex: number = -1;
    @observable.ref rawFocusedIndex: number = -1;

    DEFAULT_THEME_KEYS: DropdownMenuThemeKeys = {
        listWrapper: 'DropdownMenu',
        list: 'DropdownList',
        listItem: 'DropdownListItem',
    };

    constructor(props) {
        super(props);
    }

    @computed
    get focusedIndex(): number {
        const { selectedItemIndex } = this.props;
        return this.rawFocusedIndex < 0 && selectedItemIndex >= 0
            ? selectedItemIndex
            : this.rawFocusedIndex;
    }

    set focusedIndex(index: number) {
        this.rawFocusedIndex = index;
    }

    @computed
    get itemKeyboardProps() {
        const { keyboardEvents } = this.props;

        return {
            onKeyDown: keyboardEvents.handleKeyboardEventsFor('down'),
            onKeyUp: keyboardEvents.handleKeyboardEventsFor('up'),
        };
    }

    @computed
    get dropdownMenuHiddenStyle(): React.CSSProperties {
        const { showDropdown } = this.props;

        if (!showDropdown) {
            return {
                visibility: 'hidden',
                pointerEvents: 'none',
            };
        }
        return {};
    }

    render() {
        const {
            items,
            itemLabelFn,
            handleOnClick,
            selectedItemIndex,
            dropdownMenuRef,
            isOpen,
            childReferences,
            superscriptScope,
        } = this.props;

        return (
            <DropdownMenuWrapper
                ref={dropdownMenuRef}
                {...this.getTheme('listWrapper')}
                style={this.dropdownMenuHiddenStyle}
            >
                <DropdownList
                    role={'listbox'}
                    tabIndex={-1}
                    {...this.getTheme('list')}
                >
                    {items.map((item, i) => {
                        return (
                            <DropdownListItem
                                key={`dropdown-list-item-${i}`}
                                item={item}
                                itemLabelFn={itemLabelFn}
                                index={i}
                                isOpen={isOpen}
                                isFocused={i === this.focusedIndex}
                                instanceListener={childReferences.getChildIndexListener(
                                    i
                                )}
                                onClick={event => handleOnClick(event, item, i)}
                                dataAlternated={i % 2 ? true : false}
                                dataSelected={i === selectedItemIndex}
                                superscriptScope={superscriptScope}
                                dataMetricId={`moduleDropdown-listItem-${camelCase(
                                    item.label
                                )}`}
                                {...this.itemKeyboardProps}
                                {...this.getTheme('listItem')}
                            />
                        );
                    })}
                </DropdownList>
            </DropdownMenuWrapper>
        );
    }
}

export default withTheme(DropdownMenu);



// WEBPACK FOOTER //
// ./src/Components/Dropdown/Components/DropdownMenu.tsx